/**
 * 点击追踪模块
 * document 事件委托（capture 阶段），调用 clickCollector 提取数据
 * 支持动态配置按需采集
 */

import type { TrackData, BusinessParams, CommonParams, ClickEventParams, RoutePathResolver, ReferrerResolver, DynamicEventConfig, DynamicElementConfig } from '../types'
import { collectClickEvent, shouldTrackClick } from '../collectors/click'
import { refreshDynamicFields } from '../collectors/common'
import { resolveRoutePath, getPageId, resolveReferrerPage } from '../collectors/page'
import { Reporter } from '../reporter'
import type { DynamicConfigManager } from '../core/dynamic-config'
import { DATA_ATTRS } from '../core/constants'
import type { PageModule } from './page'
import type { AfterClickModule } from './after-click'
import { extractAttributes, collectMissingFields } from '../utils'

export interface ClickModuleConfig {
  enabled: boolean
  debug?: boolean
  routePathResolver?: RoutePathResolver
  referrerResolver?: ReferrerResolver
}

/**
 * 点击追踪模块
 */
export class ClickModule {
  private config: ClickModuleConfig
  private reporter: Reporter
  private commonParams: CommonParams
  private dynamicConfigManager?: DynamicConfigManager
  private isInitialized = false
  private pageModule?: PageModule
  private afterClickModule?: AfterClickModule

  constructor(
    config: ClickModuleConfig,
    reporter: Reporter,
    commonParams: CommonParams,
    dynamicConfigManager?: DynamicConfigManager,
    pageModule?: PageModule,
    afterClickModule?: AfterClickModule
  ) {
    this.config = config
    this.reporter = reporter
    this.commonParams = commonParams
    this.dynamicConfigManager = dynamicConfigManager
    this.pageModule = pageModule
    this.afterClickModule = afterClickModule
  }

  /**
   * 初始化模块
   */
  init(): void {
    if (!this.config.enabled || this.isInitialized) return
    if (typeof document === 'undefined') return

    this.isInitialized = true

    // 使用 capture 阶段监听，确保先于其他处理器
    document.addEventListener('click', this.handleClick, true)

    this.log('Click module initialized')
  }

  /**
   * 更新公共参数引用
   */
  updateCommonParams(commonParams: CommonParams): void {
    this.commonParams = commonParams
  }

  /**
   * 处理点击事件
   */
  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null
    // 检查是否需要追踪
    if (!shouldTrackClick(target)) {
      return
    }

    // 动态配置模式下按需检查
    if (this.dynamicConfigManager) {
      const result = this.findEleDataset(target)
      if (!result) {
        // this.log('未找到 data-tracker-ele-id，动态配置模式下跳过')
        return
      }
      const { eleId, listItemFilter } = result

      const routePath = resolveRoutePath(this.config.routePathResolver)
      const pageId = getPageId(routePath)

      // 从配置中获取元素配置
      const eleConfig = this.dynamicConfigManager.getElementConfig(pageId, eleId)

      // 获取 click 事件配置
      const clickEventConfig = this.dynamicConfigManager.getEventConfig(pageId, eleId, 'click')
      if (clickEventConfig) {
        const afterClickFields = clickEventConfig.fields?.filter(
          f => f.bindType === 'ELEMENT_AFTER'
        ) || []
        const normalClickFields = clickEventConfig.fields?.filter(
          f => f.bindType !== 'ELEMENT_AFTER'
        ) || []

        // 存在 ELEMENT_AFTER 字段 → 激活 afterClick 拦截，合并上报点击前后所有字段
        if (afterClickFields.length > 0 && this.afterClickModule) {
          this.afterClickModule.handleAfterClick(event, eleConfig, clickEventConfig)
        }

        // 存在非 ELEMENT_AFTER 字段 → 上报普通 click
        else {
          this.reportClick(event, eleConfig, {
            ...clickEventConfig,
            fields: normalClickFields,
          }, undefined, listItemFilter)
        }
      }

      return
    }

    // 代码保留
    // this.reportClick(event)
  }

  /**
   * @name findEleDataset
   * 从目标元素向上查找 eleId 及 listItemFilter
   * 向上遍历最多 5 层祖先，找到首个含 data-tracker-ele-id 的元素，
   * 同时提取该元素上的 data-tracker-list-item-filter（JSON）
   */
  private findEleDataset(element: HTMLElement | null): { eleId: string; listItemFilter: Record<string, unknown> | null } | null {
    let current: HTMLElement | null = element
    const maxDepth = 5

    for (let i = 0; i < maxDepth && current; i++) {
      const eleId = current.getAttribute(DATA_ATTRS.ELE_ID)
      if (eleId) {
        // 提取 LIST_ITEM_FILTER
        let listItemFilter: Record<string, unknown> | null = null
        const filterStr = current.getAttribute(DATA_ATTRS.LIST_ITEM_FILTER)
        if (filterStr) {
          try {
            listItemFilter = JSON.parse(filterStr)
          } catch (e) {
            this.log('LIST_ITEM_FILTER JSON 解析失败', { eleId, filterStr, error: e })
          }
        }
        return { eleId, listItemFilter }
      }
      current = current.parentElement
    }

    return null
  }

  /**
   * 上报点击事件
   */
  reportClick(
    event: MouseEvent,
    eleConfig?: DynamicElementConfig | null,
    eventConfig?: DynamicEventConfig | null,
    businessParams?: BusinessParams,
    listItemFilter?: Record<string, unknown> | null,
  ): void {

    // 获取事件参数
    const clickParams = collectClickEvent(event)
    const common = refreshDynamicFields(this.commonParams)

    // 获取页面级参数
    const pageParams = this.pageModule?.getPageParams() ?? null
    const networkRecords = this.pageModule?.getCurrentPageRequests() ?? []

    // 获取页面信息
    const routePath = resolveRoutePath(this.config.routePathResolver)
    const pageId = getPageId(routePath)
    const pageConfig = this.dynamicConfigManager?.getPageConfig(pageId)

    // 提取attributes参数
    const attributesData = extractAttributes(eventConfig?.fields || [], {
      pageParams: pageParams || {},
      eleParams: clickParams.clickEleParams,
      businessParams: businessParams,
      apis: networkRecords
    }, listItemFilter)

    // 收集 fieldList 中 value 为 null 或 undefined 的字段信息
    const missingFields = collectMissingFields(attributesData.fieldList)

    const data: TrackData = {
      common,
      event: {
        eventType: 'click',
        eventName: eventConfig?.eventName || '',
        eventCode: eventConfig?.eventCode || '',
        click: {
          eleId: eleConfig?.eleId,
          eleBusinessName: eleConfig?.eleBusinessName,
          clickPosition: clickParams?.clickPosition,
          clickScene: clickParams?.clickScene || '',
        },
        page: {
          pageId: pageId,
          pageName: pageConfig?.pageName || document.title || '学天教育',
          currentUrl: window.location.href,
          referrerPage: resolveReferrerPage(this.config.referrerResolver),
        }
      },
      business: {
        attributes: attributesData,
        missingFields,
        eventResult: {},
      }
    }

    this.reporter.report(data)
    this.log('Click reported', data)
  }

  /**
   * 手动上报点击（不依赖原生事件）
   * todo 待验证
   */
  trackClick(
    params: Partial<ClickEventParams> & { eventName?: string },
    businessParams?: BusinessParams
  ): void {
    // 动态配置模式下手动上报也受配置约束
    if (this.dynamicConfigManager) {
      this.log('Manual click in dynamic config mode, passing through')
    }

    const routePath = resolveRoutePath(this.config.routePathResolver)
    const pageId = getPageId(routePath)
    // 根据 pageId 获取当前页的配置
    const pageConfig = this.dynamicConfigManager?.getPageConfig(pageId)
    const common = refreshDynamicFields(this.commonParams)
    const eleId = params?.clickEleId != null ? String(params.clickEleId) : ''
    const { eventName, ...clickFields } = params

    const eleConfig = this.dynamicConfigManager?.getElementConfig(pageId, eleId) || null
    const pageParams = this.pageModule?.getPageParams() ?? null
    const networkRecords = this.pageModule?.getCurrentPageRequests() ?? null
    const eventConfig = this.dynamicConfigManager?.getEventConfig(pageId, eleId, 'click') || null

    // 提取attributes参数
    const attributesData = extractAttributes(eventConfig?.fields || [], {
      pageParams: pageParams || {},
      eleParams: clickFields.clickEleParams,
      businessParams: businessParams,
      apis: networkRecords || []
    })

    // 收集 fieldList 中 value 为 null 或 undefined 的字段信息
    const missingFields = collectMissingFields(attributesData.fieldList)

    const data: TrackData = {
      common,
      event: {
        eventType: 'click',
        eventName: eventConfig?.eventName || '',
        eventCode: eventConfig?.eventCode || '',
        click: {
          eleId: eleConfig?.eleId,
          eleBusinessName: eleConfig?.eleBusinessName,
          clickPosition: clickFields?.clickPosition,
          clickScene: clickFields?.clickScene || '',
        },
        page: {
          pageId: pageId,
          pageName: pageConfig?.pageName || document.title || '学天教育',
          currentUrl: window.location.href,
          referrerPage: resolveReferrerPage(this.config.referrerResolver),
        },
      },
      business: {
        attributes: attributesData,
        missingFields,
        eventResult: {},
      },
    }

    this.reporter.report(data)
    this.log('Manual click tracked', params)
  }

  /**
   * 销毁模块
   */
  destroy(): void {
    if (!this.isInitialized) return

    document.removeEventListener('click', this.handleClick, true)
    this.isInitialized = false

    this.log('Click 模块已销毁')
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log('%c[Tracker Click 调试] %c' + message, 'background: #409EFF; color: #fff;', 'color: inherit;', data)
    }
  }
}
